import { getOwnLinkedIdentity } from "@/lib/auth/linked-identity";
import { DraftEvents } from "@/lib/database/events";
import { createClient } from "@/utils/supabase/client";

import { syncEventClientToQuo } from "./actions/sync-event-client-to-quo";

/** Columns a caller may set when creating a draft — everything else on `DraftEvents` (ids, ownership
 *  fields like `org_id`/`event_id`, staff-only fields like `skip_sending_rfp`) is excluded so an open
 *  `Partial<DraftEvents>` spread can't be used to smuggle a value into a column the wizard never asks for. */
const DRAFT_EVENT_CREATABLE_FIELDS = [
  "name",
  "formatted_locations",
  "num_people",
  "accommodations",
  "meeting_rooms",
  "num_meeting_rooms_needed",
  "agenda",
  "meeting_room_dates",
  "event_dates",
  "flexibility",
  "budget_breakdown",
  "notes_location_budget",
  "decision_deadline",
  "decision_factors",
  "decision_factor_other_text",
  "additional_services",
  "custom_questions",
  "custom_concessions",
  "event_source",
  "event_source_other_text",
  "other_notes",
  "meeting_room_requirements",
  "food_beverage_requirements",
] as const satisfies readonly (keyof DraftEvents)[];

/** Filters `fields` down to {@link DRAFT_EVENT_CREATABLE_FIELDS} — shared by `createDraftEvent`'s
 *  insert and `updateDraftEvent`'s update so a caller can't smuggle an id/ownership/staff-only field
 *  into either write via an open `Partial<DraftEvents>`. */
function allowlistDraftFields(fields: Partial<DraftEvents>): Partial<DraftEvents> {
  const allowed: Partial<DraftEvents> = {};
  for (const key of DRAFT_EVENT_CREATABLE_FIELDS) {
    if (fields[key] !== undefined) (allowed as Record<string, unknown>)[key] = fields[key];
  }
  return allowed;
}

/**
 * The address stored on the caller's linked account, which draft rows are keyed by. Resolved
 * through the login's link, never the session's email claim (ENG-726). RLS compares
 * `draft_events.user_email` against the same linked address.
 *
 * @param supabase - The browser Supabase client.
 * @returns The linked account's email.
 * @throws When there is no session or the login is linked to no account.
 */
async function getCallerEmail(supabase: ReturnType<typeof createClient>): Promise<string> {
  const identity = await getOwnLinkedIdentity(supabase);
  if (!identity) throw new Error("User not authenticated");

  return identity.email;
}

export async function getDraftEvents(): Promise<DraftEvents[]> {
  const supabase = createClient();

  const callerEmail = await getCallerEmail(supabase);

  // Fetch draft events from draft_events table for the current user
  // Draft events are associated with users via their linked account's email
  const { data: drafts, error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .select("*")
    .eq("user_email", callerEmail)
    .is("event_id", null) // Only get drafts that haven't been converted to real events
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching draft events:", error);
    throw error;
  }

  return drafts || [];
}

/**
 * Creates a draft event in a single insert. `fields` carries any initial column values beyond the
 * name — for callers (like the create-event-v3 wizard's first autosave) that already have more than
 * just a name to persist and would otherwise need a follow-up `updateDraftEvent` call. `fields.name`
 * falls back to "Untitled Event" when omitted or blank.
 *
 * @param fields - Initial `draft_events` column values (name optional).
 * @returns The new draft's id.
 */
export async function createDraftEvent(fields: Partial<DraftEvents> = {}): Promise<{ draft_event_id: number }> {
  const supabase = createClient();

  const callerEmail = await getCallerEmail(supabase);

  // Create a new draft event with the linked account's email — only allowlisted columns pass through, so a caller
  // can't smuggle an id/ownership/staff-only field into the insert via `fields`.
  const allowedFields = allowlistDraftFields(fields);

  const { data: event, error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .insert({
      ...allowedFields,
      name: (typeof fields.name === "string" && fields.name) || "Untitled Event",
      user_email: callerEmail,
      created_at: new Date().toISOString(),
    })
    .select("draft_event_id")
    .single();

  if (error) {
    console.error("Error creating draft event:", error);
    throw error;
  }

  // Fire-and-forget: ensure the creator has a Quo contact (adds if missing, patches
  // if their phone/title changed). Failure here must not block event creation.
  syncEventClientToQuo().catch((err) => {
    console.error("createDraftEvent: Quo client sync failed", err);
  });

  return event;
}

export async function deleteDraftEvent(draftEventId: number): Promise<void> {
  const supabase = createClient();

  const callerEmail = await getCallerEmail(supabase);

  // Delete the draft event (only if user is the creator)
  const { error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .delete()
    .eq("draft_event_id", draftEventId)
    .eq("user_email", callerEmail);

  if (error) {
    console.error("Error deleting draft event:", error);
    throw error;
  }
}

/**
 * Updates a draft event's columns. Callers so far never read the updated row back (autosave just
 * needs the write to land), so this returns `void` rather than paying for a `.select("*")` round
 * trip on every call — draft rows carry several large jsonb blobs (agenda, accommodations,
 * event_dates, budget_breakdown), and this runs on a debounced hot path during the wizard fill.
 *
 * @param draftEventId - The draft to update.
 * @param updates - Column values to write — filtered through {@link DRAFT_EVENT_CREATABLE_FIELDS}
 *   so a caller can't smuggle an id/ownership/staff-only field into the update, matching `createDraftEvent`.
 */
export async function updateDraftEvent(draftEventId: number, updates: Partial<DraftEvents>): Promise<void> {
  const supabase = createClient();

  const callerEmail = await getCallerEmail(supabase);

  const allowedUpdates = allowlistDraftFields(updates);

  // Update the draft event (only if user is the creator)
  const { error } = await supabase
    .schema("nowadays")
    .from("draft_events")
    .update(allowedUpdates)
    .eq("draft_event_id", draftEventId)
    .eq("user_email", callerEmail);

  if (error) {
    console.error("Error updating draft event:", error);
    throw error;
  }
}
